import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { dayjs } from '@/extendedDayjs';
import Avatar from 'antd/lib/avatar';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Popconfirm from 'antd/lib/popconfirm';
import Popover from 'antd/lib/popover';
import Space from 'antd/lib/space';
import Table from 'antd/lib/table';
import Tag from 'antd/lib/tag';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import { keyBy, uniq } from 'lodash';
import { DeleteOutlined, EditOutlined, KeyOutlined, PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { OrgUpdatable } from '@/actions/organization';
import { clearApiKey } from '@/actions/publicApi';
import { getAvailableAppsFromAppDataMap } from '@/helpers';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { useRegenerateAgentAPIKeyMutation } from '@/middlewares/defaultApi';
import { getMdmPlatformOverride, platformLabelForApp } from '@/helpers/mdmPlatformOverride';
import { RootState } from '@/reducers/reducers';
import {
    selectOrg,
    selectOrgSettings,
    selectPublicApiKey,
    selectPublicApiKeyWarning,
    selectUserAppDataByAppIdMap,
} from '@/selectors/selectors';
import { DatetimeFormat } from '@/timeHelpers';
import { Agent, AgentType, RunwayResponseType, SubscriptionFeatureType } from '@/types';
import { globalMessage } from '@/utils/globalMessage';
import { useUserCanPerformOrgAction } from '@/utils/hooks/useUserCanPerformAction';
import { useCallUpdatable } from '@/utils/hooks/helpers';
import { PlanRestrictedFeatureWrapper } from '../planRestrictions/PlanRestrictedFeatureWrapper';
import { antGrey, antRed } from '../styles/globalStyles';
import WorkflowsPlatformIcon from '../icons/WorkflowsPlatformIcon';
import { UserAppData } from '@/typescriptHelpers';

import AgentKeyModal from './AgentKeyModal';
import AgentModal from './AgentModal';
import { agentSampleWorkflowName, isSampleCIKind } from './agentSetupHelpers';
import RecreateAgentWebhookModal from './RecreateAgentWebhookModal';

interface AgentsSettingsProps {
    orgId: string;
}

type AgentModalState = { open: false } | { open: true; agent?: Agent };

const AGENT_TYPE_LABELS: Record<AgentType, string> = {
    [AgentType.CustomWebhook]: 'Agent service · Standard webhook',
    [AgentType.ClaudeRoutine]: 'Agent service · Claude Code routine',
    [AgentType.CIWorkflow]: 'CI workflow',
};

const MAX_APP_ICONS_SHOWN = 3;

const APP_ICON_SIZE = 18;

const actionButtonStyle: React.CSSProperties = { color: antGrey.grey10, display: 'flex', alignItems: 'center' };

const DisabledReasonTooltip: React.FC<{ reason?: string; children: React.ReactNode }> = ({ reason, children }) =>
    reason ? <Tooltip title={reason}>{children}</Tooltip> : <>{children}</>;

const AgentsSettings: React.FC<AgentsSettingsProps> = ({ orgId }) => {
    const dispatch = useDispatch();
    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const webhooks = useSelector((state: RootState) => selectOrgSettings(state, orgId)?.webhooks ?? []);
    const appDataByAppIdMap = useSelector((state: RootState) => selectUserAppDataByAppIdMap(state, orgId));
    const agents = React.useMemo(() => (org?.agents ?? []).filter((agent) => !agent.deletedAt), [org?.agents]);
    const webhooksById = React.useMemo(() => keyBy(webhooks, (webhook) => webhook.id), [webhooks]);
    const canManage = !!useUserCanPerformOrgAction(RunwayUserAction.UACreateDeleteAPITokens);
    const noPermissionReason = canManage ? undefined : "You don't have permission to manage API keys.";
    const editDisabledReason = (agent: Agent) =>
        agent.type === AgentType.CustomWebhook && !webhooksById[agent.webhookEndpointId] ?
            'Recreate the webhook first.'
        :   undefined;

    const [modalState, setModalState] = React.useState<AgentModalState>({ open: false });
    const [recreateAgent, setRecreateAgent] = React.useState<Agent | null>(null);
    const [keyAgent, setKeyAgent] = React.useState<Pick<Agent, 'name' | 'type'> | null>(null);
    const issuedKeyValue = useSelector(selectPublicApiKey);
    const issuedKeyWarning = useSelector(selectPublicApiKeyWarning);
    const [regenerateAgentAPIKey, { isLoading: regenerateIsLoading, originalArgs: regenerateArgs }] =
        useRegenerateAgentAPIKeyMutation();
    const [actionsAgentId, setActionsAgentId] = React.useState<string | null>(null);
    const closeModal = React.useCallback(() => setModalState({ open: false }), []);
    const editedAgentId = modalState.open ? modalState.agent?.id : undefined;
    const otherAgentNames = React.useMemo(
        () => agents.filter((agent) => agent.id !== editedAgentId).map((agent) => agent.name.toLowerCase()),
        [agents, editedAgentId],
    );
    const closeRecreateModal = React.useCallback(() => setRecreateAgent(null), []);
    const [deleteAgent, deleteAgentIsLoading, deleteAgentResponseType, deleteAgentError] = useCallUpdatable(
        OrgUpdatable.DeleteAgent,
        { orgId },
    );

    React.useEffect(() => {
        if (deleteAgentResponseType === RunwayResponseType.Success) {
            globalMessage.success('Agent deleted.');
        }
    }, [deleteAgentResponseType]);

    React.useEffect(() => {
        if (deleteAgentError) {
            globalMessage.error(deleteAgentError);
        }
    }, [deleteAgentError]);

    const onRegenerateKey = async (agent: Agent) => {
        setKeyAgent(agent);
        const result = await regenerateAgentAPIKey({ orgId, agentId: agent.id });
        const response = 'data' in result ? result.data : undefined;
        if (response?.type !== RunwayResponseType.Success) {
            globalMessage.error(response?.message || 'Error regenerating API key');
        }
    };

    const closeKeyModal = () => {
        setKeyAgent(null);
        dispatch(clearApiKey());
    };

    const renderTitle = (title: string) => (
        <Text strong style={{ whiteSpace: 'nowrap' }}>
            {title}
        </Text>
    );

    const renderApps = (appIds: string[]) => {
        const apps = getAvailableAppsFromAppDataMap({ appDataMap: appDataByAppIdMap, appIds }) ?? [];
        if (appIds.length === 0) {
            return <Text type="secondary">No apps</Text>;
        }
        if (apps.length === 0) {
            return <Text type="secondary">{appIds.length} apps</Text>;
        }
        const appsToDisplay = apps.slice(0, MAX_APP_ICONS_SHOWN);
        const remainingApps = apps.slice(MAX_APP_ICONS_SHOWN);
        const appLabel = (appData: UserAppData) => `${appData.appName} (${platformLabelForApp(appData)})`;
        return (
            <Space size={4} wrap style={{ rowGap: 4 }}>
                {appsToDisplay.map((appData) => (
                    <Tooltip key={appData.appId} title={appLabel(appData)}>
                        <span style={{ display: 'flex' }}>
                            {appData.appImageUrl ?
                                <Avatar
                                    shape="square"
                                    size={APP_ICON_SIZE}
                                    src={appData.appImageUrl}
                                    style={{ borderRadius: 4, flexShrink: 0 }}
                                >
                                    {appData.appName.charAt(0).toUpperCase()}
                                </Avatar>
                            :   (getMdmPlatformOverride(appData.shouldDisplayAsMdm)?.icon(APP_ICON_SIZE) ?? (
                                    <WorkflowsPlatformIcon
                                        platform={appData.platform}
                                        width={APP_ICON_SIZE}
                                        height={APP_ICON_SIZE}
                                    />
                                ))
                            }
                        </span>
                    </Tooltip>
                ))}
                {remainingApps.length > 0 && (
                    <Tooltip
                        title={remainingApps.map((appData) => (
                            <div key={appData.appId}>{appLabel(appData)}</div>
                        ))}
                    >
                        <Text type="secondary">+{remainingApps.length}</Text>
                    </Tooltip>
                )}
            </Space>
        );
    };

    const renderDelivery = (_: unknown, agent: Agent) => {
        if (agent.type === AgentType.CIWorkflow && isSampleCIKind(agent.ciKind)) {
            return <Text>Workflow &quot;{agentSampleWorkflowName(agent)}&quot;</Text>;
        }
        if (agent.type === AgentType.CIWorkflow) {
            const workflowNames = uniq(agent.appIds.map((appId) => agent.ciWorkflows?.[appId]?.workflow.workflowName));
            if (workflowNames.length === 1) {
                return <Text>Workflow &quot;{workflowNames[0]}&quot;</Text>;
            }
            return (
                <Space direction="vertical" size={0}>
                    {agent.appIds.map((appId) => (
                        <Text key={appId}>
                            Workflow &quot;{agent.ciWorkflows?.[appId]?.workflow.workflowName}&quot; in{' '}
                            {appDataByAppIdMap[appId]?.appName ?? appId}
                        </Text>
                    ))}
                </Space>
            );
        }
        if (agent.type === AgentType.ClaudeRoutine) {
            return <Text>Claude Code routine</Text>;
        }
        const webhook = webhooksById[agent.webhookEndpointId];
        if (webhook) {
            return <Text>{webhook.url}</Text>;
        }
        return (
            <Space>
                <Tag color="error">Webhook missing</Tag>
                <DisabledReasonTooltip reason={noPermissionReason}>
                    <Button type="link" size="small" disabled={!canManage} onClick={() => setRecreateAgent(agent)}>
                        Recreate
                    </Button>
                </DisabledReasonTooltip>
            </Space>
        );
    };

    const renderActions = (_: unknown, agent: Agent) => (
        <Popover
            overlayClassName="org-settings-popover"
            overlayStyle={{ paddingBottom: '0px' }}
            trigger="click"
            placement="topRight"
            open={actionsAgentId === agent.id}
            onOpenChange={(open) => setActionsAgentId(open ? agent.id : null)}
            destroyTooltipOnHide
            content={
                <div style={{ width: 168 }}>
                    <DisabledReasonTooltip reason={noPermissionReason ?? editDisabledReason(agent)}>
                        <Button
                            type="link"
                            disabled={!canManage || !!editDisabledReason(agent)}
                            icon={<EditOutlined style={{ color: antGrey.grey7 }} />}
                            style={actionButtonStyle}
                            onClick={() => {
                                setActionsAgentId(null);
                                setModalState({ open: true, agent });
                            }}
                        >
                            Edit agent
                        </Button>
                    </DisabledReasonTooltip>
                    <DisabledReasonTooltip reason={noPermissionReason}>
                        <Popconfirm
                            title="Create a new API key and revoke the current one? The agent can't read issues until you give it the new key."
                            placement="topRight"
                            okText="Regenerate"
                            overlayStyle={{ maxWidth: 360 }}
                            disabled={!canManage}
                            onConfirm={() => {
                                setActionsAgentId(null);
                                onRegenerateKey(agent);
                            }}
                        >
                            <Button
                                type="link"
                                disabled={!canManage}
                                loading={regenerateIsLoading && regenerateArgs?.agentId === agent.id}
                                icon={<KeyOutlined style={{ color: antGrey.grey7 }} />}
                                style={actionButtonStyle}
                            >
                                Regenerate key
                            </Button>
                        </Popconfirm>
                    </DisabledReasonTooltip>
                    <DisabledReasonTooltip reason={noPermissionReason}>
                        <Popconfirm
                            title="Delete this agent and its API key? Issues already assigned to it keep the assignment."
                            placement="topRight"
                            okText="Delete"
                            overlayStyle={{ maxWidth: 360 }}
                            okButtonProps={{ loading: deleteAgentIsLoading }}
                            disabled={!canManage}
                            onConfirm={() => {
                                setActionsAgentId(null);
                                deleteAgent({ agentId: agent.id });
                            }}
                        >
                            <Button
                                type="link"
                                disabled={!canManage}
                                icon={<DeleteOutlined style={{ color: antRed.red4 }} />}
                                style={{ ...actionButtonStyle, color: antRed.red5 }}
                            >
                                Delete agent
                            </Button>
                        </Popconfirm>
                    </DisabledReasonTooltip>
                </div>
            }
        >
            <SettingOutlined style={{ fontSize: '17px', paddingInlineEnd: '30px', color: antGrey.grey8 }} />
        </Popover>
    );

    return (
        <>
            <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginTop: 30 }}>
                <Text style={{ fontSize: 20, color: antGrey.grey7, whiteSpace: 'nowrap' }}>Agents</Text>
                <div style={{ flex: 1, height: '100%' }}>
                    <Divider type="horizontal" style={{ width: '100%', margin: 0 }} />
                </div>
            </div>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 24,
                    marginTop: 15,
                    paddingInlineEnd: 16,
                }}
            >
                <Text type="secondary">Coding agents that Triage issues can be assigned to.</Text>
                <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Triage} requiresTooltip>
                    {(isDisabled) => (
                        <DisabledReasonTooltip reason={noPermissionReason}>
                            <Button
                                type="primary"
                                disabled={isDisabled || !canManage}
                                icon={<PlusOutlined />}
                                onClick={() => setModalState({ open: true })}
                            >
                                Create agent
                            </Button>
                        </DisabledReasonTooltip>
                    )}
                </PlanRestrictedFeatureWrapper>
            </div>
            <Table
                rowKey="id"
                dataSource={agents}
                columns={[
                    { title: renderTitle('Name'), dataIndex: 'name' },
                    {
                        title: renderTitle('Type'),
                        dataIndex: 'type',
                        render: (type: AgentType) => AGENT_TYPE_LABELS[type] ?? type,
                    },
                    { title: renderTitle('Apps'), dataIndex: 'appIds', render: renderApps },
                    { title: renderTitle('Delivery'), key: 'delivery', render: renderDelivery },
                    { title: renderTitle('Created by'), dataIndex: 'createdBy' },
                    {
                        title: renderTitle('Created at'),
                        dataIndex: 'createdAt',
                        render: (date: string) =>
                            dayjs(date).format(DatetimeFormat.DayAbbreviatedMonthDateYearTimeAbbreviated),
                    },
                    { title: '', key: 'actions', align: 'right', render: renderActions },
                ]}
                locale={{ emptyText: 'No agents yet' }}
                pagination={{ hideOnSinglePage: true, defaultPageSize: 10 }}
                style={{ marginTop: 24, marginBottom: 24 }}
                scroll={{ x: 1100 }}
            />
            {modalState.open && (
                <AgentModal
                    orgId={orgId}
                    agent={modalState.agent}
                    webhook={modalState.agent ? webhooksById[modalState.agent.webhookEndpointId] : undefined}
                    otherAgentNames={otherAgentNames}
                    onClose={closeModal}
                />
            )}
            {recreateAgent && (
                <RecreateAgentWebhookModal orgId={orgId} agent={recreateAgent} onClose={closeRecreateModal} />
            )}
            {keyAgent && issuedKeyValue && (
                <AgentKeyModal
                    issuedKey={{
                        agentName: keyAgent.name,
                        agentType: keyAgent.type,
                        apiKey: issuedKeyValue,
                        warning: issuedKeyWarning,
                    }}
                    onClose={closeKeyModal}
                />
            )}
        </>
    );
};

export default AgentsSettings;
